import type { OrgGroup } from '../../contracts/account';
import {
  canonicaliseOrgSlug,
  isValidOrgSlug,
  PERSONAL_SLUG,
} from '../auth/org-slug';
import type { SwitcherItem } from '../auth/workspace-context';

// ---------------------------------------------------------------------------
// Org-scope selection — a multi-select narrowing of the Personal ("all orgs")
// workspace down to a subset of the viewer's orgs, applied across every view
// (work items + reviews). Encoded in the URL as `?orgs=a,b` (comma-joined,
// sorted org slugs); an empty selection means the whole personal aggregate.
//
// These are the pure helpers (server- and client-safe): the URL codec, a nav
// query suffix, and the per-view directory narrowing. The reactive store and the
// `replaceState` writer live in `src/stores/org-scope.ts`. Mirrors the codec
// style of `components/work-items/work-item-filters.ts`.
// ---------------------------------------------------------------------------

/** Split a comma list, trimming blanks (`"a, ,b"` -> `["a","b"]`, `""` -> `[]`). */
function parseList(raw: string): readonly string[] {
  return raw
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '');
}

function compareCodePoints(left: string, right: string): number {
  if (left < right) return -1;
  return left > right ? 1 : 0;
}

/**
 * Parse the selected-org slugs from query params. Each is canonicalised
 * (lower-cased) and kept only when it's a real, addressable org slug —
 * `PERSONAL_SLUG` (which `isValidOrgSlug` deliberately accepts) and
 * syntactically-invalid/reserved slugs are dropped. When `knownSlugs` is given, a
 * slug must also be one the viewer actually belongs to, so a stale or hand-typed
 * `?orgs=foo` can't strand the board on a permanently-empty scope. Returns a
 * deduped, sorted array (`[]` = the whole personal aggregate).
 */
export function readSelectedOrgs(
  parameters: URLSearchParams,
  knownSlugs?: ReadonlySet<string>,
): readonly string[] {
  const raw = parameters.get('orgs');
  if (raw === null) return [];
  const slugs = parseList(raw)
    .map((slug) => canonicaliseOrgSlug(slug))
    .filter(
      (slug) =>
        slug !== PERSONAL_SLUG &&
        isValidOrgSlug(slug) &&
        (knownSlugs === undefined || knownSlugs.has(slug)),
    );
  return [...new Set(slugs)].toSorted(compareCodePoints);
}

/** Serialize the selection for the URL, or `null` to omit the param (the default). */
export function serializeSelectedOrgs(orgs: readonly string[]): string | null {
  if (orgs.length === 0) return null;
  return [...new Set(orgs)].toSorted(compareCodePoints).join(',');
}

/** A `?orgs=…` query suffix for a nav link, or `''` when nothing is selected. */
export function orgScopeQuery(orgs: readonly string[]): string {
  const serialized = serializeSelectedOrgs(orgs);
  return serialized === null ? '' : `?orgs=${serialized}`;
}

/**
 * Narrow a workspace's discovered org list to the selected scope, comparing
 * case-insensitively (the directory keeps GitHub's casing while slugs are
 * lower-cased) while preserving the directory's own casing. An empty selection
 * means the whole directory.
 */
export function effectiveDirectoryOrgs(
  directoryOrgs: readonly string[],
  selected: readonly string[],
): readonly string[] {
  if (selected.length === 0) return directoryOrgs;
  const wanted = new Set(selected.map((slug) => slug.toLowerCase()));
  return directoryOrgs.filter((org) => wanted.has(org.toLowerCase()));
}

/**
 * The selected-org key for a work-item scope. GitHub scopes use their owner;
 * Linear workspaces use the paired GitHub org when one exists.
 */
export function orgOfWorkItemScope(
  scope: string,
  provider: 'github' | 'linear',
  orgGroups: readonly Readonly<OrgGroup>[],
): string {
  const owner = (scope.split('/')[0] ?? scope).toLowerCase();
  if (provider === 'github') return owner;
  const mapped = orgGroups.find(
    (group) => group.linearWorkspace.toLowerCase() === owner,
  );
  return mapped === undefined
    ? `linear ${owner}`
    : mapped.githubOrg.toLowerCase();
}

/** True when two selections hold the same slugs, order-independent. */
export function sameOrgSet(
  a: readonly string[],
  b: readonly string[],
): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((slug) => set.has(slug));
}

/**
 * Exactly-one-org scope keeps org-only surfaces reachable from the personal
 * shell only when the selection maps to a Clerk-backed workspace. PAT-only
 * GitHub orgs are valid Personal filters but have no addressable org shell.
 */
export function selectedOrgOf(
  slug: string,
  selectedOrgs: readonly string[],
): string | null {
  if (slug !== PERSONAL_SLUG || selectedOrgs.length !== 1) return null;
  return selectedOrgs[0]?.toLowerCase() ?? null;
}

/** The addressable workspace slug behind a single-org selection, if any. */
export function scopedOrgOf(
  selectedOrg: string | null,
  items: readonly SwitcherItem[],
): string | null {
  if (selectedOrg === null) return null;
  const workspace = items.find(
    (item) =>
      item.organizationId !== null &&
      (item.githubLogin ?? item.slug).toLowerCase() === selectedOrg,
  );
  return workspace?.slug ?? null;
}
